import * as Bixby from '$/core';
import { LoggerService } from '$/services/LoggerService';
import { Location } from 'history';

export class CscpIntegrationService {

  private static readonly adminRoute = 'admin';
  private static readonly loginRoute = 'login';
  private static readonly logoutRoute = 'logout';

  /** We need to save initial page request for redirect after login redirect completes successfully, but not for these. */
  private static readonly lastDirPathsToIgnore = [this.adminRoute, this.loginRoute, this.logoutRoute];
  /** We get the chance to rewrite the redirect right after path login completes with is Authenticated. */
  private static readonly pathsToOverwriteWithInitRequest = [this.adminRoute, this.logoutRoute];

  /**
   * CSCP will set a query param of key d and value timestamp epoch time, then we know this is a CSCP set value
   * @param location The location received when processing all state updates.
   * @returns {boolean} Based on if location is from CSCP or not.
   */
  private static locationIsFromCscp = (location: Location): boolean => {
    const queryParams = location.search;
    if (queryParams) {
      const removedQ = queryParams.replace('?', '');
      const keyValuePairs = removedQ.split('&');
      const key = keyValuePairs.find(kvPair => kvPair.split('=')[0] === 'd');
      if (key?.includes('d=')) {
        LoggerService.log(`
          locationIsFromCscp: 
            Received location from CSCP based on query param ${location.search}`);
        return true;
      }
    }
    LoggerService.log(`
        locationIsFromCscp: 
          Received internal app location based on no query param ${location.search}`);
    return false;
  };

  /** Removes base path, leading and trailing slashes for normalized comparisons. */
  private static normalizePath = (location: Location): string => {
    const bixbyRouteSanitizedFromPathPrefix = location.pathname.replace(Bixby.Configuration.cscpIframePathPrefix, '');
    const leadingSlashNormalized = this.removeLeadingSlash(bixbyRouteSanitizedFromPathPrefix);
    const normalizedPath = this.removeTrailingSlash(leadingSlashNormalized);
    LoggerService.log(`normalizePath: ${normalizedPath}`);
    return normalizedPath;
  };

  /**
   * Removes a leading forward slash from a path string if one exists.
   * @param {string} path The input path string.
   * @returns {string} The path string without a leading slash.
   */
  private static removeLeadingSlash = (path: string) => {
    if (path.startsWith('/')) {
      return path.substring(1);
    }
    return path;
  };

  private static getInitialRequestPath(): string | null {
    return localStorage.getItem(Bixby.Constants.INITIAL_PAGE_REQUEST_PATH);
  }
  /**
   * Removes a trailing forward slash from a path string if one exists.
   * @param {string} path  The input path string
   * @returns {string} The path string without a trailing slash.
  */
  private static removeTrailingSlash = (path: string) => path.replace(/\/+$/, '');
  
  //#region PUBLIC API
  /**
   * On initial page request, when in CSCP the iframe will receive the source from the parent window,
   * and it'll be lost on redirect, so we must save it, and use it later.
   * */
  public static processLocationForMsal(location: Location) {
    LoggerService.log(`
      processLocationForMsal:
        begin processing location change for msal.`)
    // If we get the initial src passed to the iframe after redirect, save it in local storage
    const normalizedPath = this.normalizePath(location);
    const isCscpPath = this.locationIsFromCscp(location);
    const lastDir = normalizedPath.split('/').pop() ?? '';
    const pathShouldBeIgnored = this.lastDirPathsToIgnore.includes(lastDir);
    if (!!normalizedPath && isCscpPath && !pathShouldBeIgnored) {
      // Save the inital path request if it's not the root, is set by CSCP and shouldn't be ignored
      localStorage.setItem(Bixby.Constants.INITIAL_PAGE_REQUEST_PATH, normalizedPath);
      LoggerService.log(`
        processLocationForMsal: 
          set item for path: ${normalizedPath} 
        `);
    } else {
      LoggerService.log(`
        processLocationForMsal: 
        skip item for path: ${normalizedPath} 
          `);
    }
    //
  }
  public static removeInitialRequestPath(): void {
    LoggerService.log(`Removing CSCP_PATH_SET: ${this.getInitialRequestPath()}`);
    localStorage.removeItem(Bixby.Constants.INITIAL_PAGE_REQUEST_PATH);
  }
  /** 
   * Handles logic for determining if parent window has a path that we should use instaed of /admin 
   * after login Bixby Admin in CS must land on admin page as that's all they should be able to see as of 2025-10-27 
   */
  public static getRedirectPath = (readyForRedirect: boolean, currentPath: string): string => {
    let newPath = this.adminRoute;
    // If current path is any of the ignored items, return that
    if (this.pathsToOverwriteWithInitRequest.some(p => currentPath.includes(p))) {
      LoggerService.log(`getRedirectPath: returning new path: ${newPath}`)
      return newPath
    }
    // Since parent window receives initial page requests, check that before redirect to admin
    const initialPathRequest = this.getInitialRequestPath();
    if (initialPathRequest && readyForRedirect) {
      // Remove any cscp query params
      const pathOnly = initialPathRequest?.split('?')[0];
      // Remove path prefix
      const bixbyRouteSanitizedFromPathPrefix = pathOnly.replace(Bixby.Configuration.cscpIframePathPrefix, '');
      // If path is / after removing parent related aspects Ignore
      if (bixbyRouteSanitizedFromPathPrefix === '/') {
        LoggerService.log(`getRedirectPath: returning new path: ${newPath}`)
        return newPath
      }
      // Overwrite usual admin redirect
      newPath = bixbyRouteSanitizedFromPathPrefix;
    }
    return newPath
  };

  public static postMessageHandler = ({ location }) => {
    if (window.parent) {
      const cscpSetPath = this.getInitialRequestPath();
      const normalizedLocation = this.normalizePath(location);
      if (cscpSetPath === normalizedLocation) {
        LoggerService.log(`postMessageHandler skipping emitting same location we're at right now`);
        return;
      }
      const processBasePathForLocation = location.pathname.replace(Bixby.Configuration.cscpIframePathPrefix, '');
      // If we're at root we don't need to emit 
      if(!processBasePathForLocation){
        LoggerService.log(`postMessageHandler skipping emitting root`);
        return
      }
      const windowMessage: Bixby.Models.CustomIframeMessage<string> = {
        event: Bixby.Constants.MsalAppEvents.MsalAppRouteChange,
        // Prefix with slash since that's what common web expects.
        payload: `/${processBasePathForLocation}`,
      };
      // We don't need to update CSCP abbix-link link_underlineout auth related redirects
      const splitUri = globalThis.location.pathname.split('/');
      const lastDir = splitUri.at(-1);
      const noTrailingSlashPrefix = this.removeTrailingSlash(Bixby.Configuration.cscpIframePathPrefix);
      const lastDirOfPathPrefix = noTrailingSlashPrefix.split('/').at(-1);
      //Sometimes login is in the third directory with the fourth being a URI encoded redirect
      const loginDirIsInPath = splitUri.some(uri => uri === this.loginRoute);
      if (

        lastDir === this.loginRoute ||
        loginDirIsInPath ||
        lastDir === this.logoutRoute ||
        lastDir === lastDirOfPathPrefix
      ) {
        LoggerService.log(`postMessageHandler: skipping emitting path: ${location.pathname}`);
        return;
      }
      LoggerService.log(`postMessageHandle: emitting path: ${windowMessage.payload}`);
      globalThis.parent.postMessage(windowMessage, '*');
    }
  };
  //#endregion
}
